import { useLazyQuery, useMutation, gql } from '@apollo/client'
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material'
import { IconButton, InputAdornment } from '@mui/material'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import {
  SIGN_IN_FULFILLED,
  ADD_JWT_TO_STATE,
} from '@promoboxx/redux-stores/src/stores/auth/actions'
import * as Facebook from 'fb-sdk-wrapper'
import { Formik, Form, Field } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import queryString from 'query-string'
import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import * as Yup from 'yup'

import { jwtRef } from '@src/config/ApolloClient'
import LinkChild from '@src/lib/LinkChild'

import Analytics from '../../../analytics/Analytics'
import { Button } from '../../shared'
import Loading from '../../shared/Loading'
import AuthCardHeader from '../AuthCardHeader'
import { GET_JWT, USER_FIELDS_FRAGMENT } from '../saneLogin'

const GET_FACEBOOK_AUTH = gql`
  query getFacebookAuth($signedRequest: String!) {
    getFacebookAuth(signedRequest: $signedRequest) {
      jwt
    }
  }
`

export const VALIDATE_TOKEN = gql`
  ${USER_FIELDS_FRAGMENT}
  query validateTokenLogin {
    validateToken {
      user {
        ...UserFieldsFragment
      }
      headers {
        accessToken
        tokenType
        client
        expiry
        uid
      }
    }
  }
`

const CONNECT_USER = gql`
  mutation upsertFacebookUser($uuid: ID!, $signedRequest: String!) {
    upsertFacebookUser(uuid: $uuid, signedRequest: $signedRequest) {
      uuid
      signedRequest
    }
  }
`

const SIGN_IN = gql`
  ${USER_FIELDS_FRAGMENT}
  mutation signIn($email: String!, $password: String!) {
    signIn(email: $email, password: $password) {
      user {
        ...UserFieldsFragment
      }
      headers {
        accessToken
        tokenType
        client
        expiry
        uid
      }
    }
  }
`

const useStyles = makeStyles()(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  input: {
    width: '100%',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  hidden: {
    display: 'none',
  },
  loading: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

const schema = Yup.object().shape({
  email: Yup.string()
    .email()
    .required(
      I18n.t('validations.required', { label: I18n.t('shared.emailAddress') }),
    ),
  password: Yup.string().required(
    I18n.t('validations.required', { label: I18n.t('shared.password') }),
  ),
})

const permissions = [
  'public_profile',
  'email',
  'pages_manage_posts',
  'pages_manage_ads',
  'read_insights',
  'ads_management',
  'business_management',
]

// signIn(get jwt) -> Connect user(use jwt) -> Set JWT(redux logged in) -> Get User()
// facebook log in(get jwt) -> Set JWT(redux logged in) -> Get User()

const LoginForm = () => {
  const { classes } = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const facebookRef = useRef(null)
  const [socialLoginEnabled, setSocialLoginEnabled] = useState(true)

  const [facebookSignedRequest, setFacebookSignedRequest] = useState(null)
  const [pendingConnection, setPendingConnection] = useState(false)
  const [pendingLogin, setPendingLogin] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const [getFacebookAuth, getFacebookAuthResults] =
    useLazyQuery(GET_FACEBOOK_AUTH)
  const [getJwt] = useLazyQuery(GET_JWT, {
    fetchPolicy: 'no-cache',
  })
  const [validateToken] = useLazyQuery(VALIDATE_TOKEN)
  const [connectUser] = useMutation(CONNECT_USER)
  const [signIn] = useMutation(SIGN_IN)
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    if (
      getFacebookAuthResults.data &&
      getFacebookAuthResults.data.getFacebookAuth
    ) {
      setPendingLogin(true)
      addJwtToState(getFacebookAuthResults.data.getFacebookAuth.jwt)
      jwtRef.current = getFacebookAuthResults.data.getFacebookAuth.jwt
      validateToken()
        .then((validateTokenResponse) => {
          Analytics.track('Login', { type: 'Facebook Login' })
          updateUserState(validateTokenResponse.data.validateToken)
        })
        .catch((error) => {
          console.error(error)
          setPendingLogin(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFacebookAuthResults.data])

  useEffect(() => {
    if (getFacebookAuthResults.error) {
      setPendingConnection(true)
    }
  }, [getFacebookAuthResults.error])

  useEffect(() => {
    if (facebookRef) {
      try {
        const FB = Facebook.getGlobalFB()
        FB.XFBML.parse(facebookRef.current)
        FB.Event.subscribe('auth.statusChange', onFacebookStatusChange)
      } catch (error) {
        console.error(error)
        setSocialLoginEnabled(false) //If there was a problem parsing the DOM and no button would appear, hide the whole area.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingConnection, facebookRef])

  const onCancel = () => {
    //The auth.statusChange event never fires, because the status doesn't change the second time around.
    //Logging out helps that. It doesn't technically log the user out, but more so logs them out in scope of this button.
    Facebook.getGlobalFB().logout()
    setPendingConnection(false)
  }

  const onFacebookStatusChange = (response) => {
    if (response.status === 'connected') {
      setFacebookSignedRequest(response.authResponse.signedRequest)
      getFacebookAuth({
        variables: {
          signedRequest: response.authResponse.signedRequest,
        },
      })
    }
  }

  // This is what ultimately tells redux and the app we are logged in...
  const updateUserState = (payload) => {
    // Can we put validate_auth into GraphQL and just use that?
    dispatch({
      type: SIGN_IN_FULFILLED,
      payload: {
        user: payload.user,
        headers: {
          'access-token': payload.headers.accessToken,
          'token-type': payload.headers.tokenType,
          'client': payload.headers.client,
          'expiry': payload.headers.expiry,
          'uid': payload.headers.uid,
        },
      },
    })

    const doesUserHaveOpenInvitations =
      Boolean(
        payload.user.access_invitations &&
          payload.user.access_invitations.length,
      ) ||
      (payload.user.access_requests && payload.user.access_requests.length) ||
      (payload.user.actionable_access_requests &&
        payload.user.actionable_access_requests.length)
    const queryParams = queryString.parse(location.search)
    const isUserInternal = Boolean(
      payload.user.user_roles &&
        (payload.user.user_roles.includes('admin') ||
          payload.user.user_roles.includes('client_services')),
    )
    if (
      !isUserInternal &&
      (!payload.user.retailer_accounts ||
        payload.user.retailer_accounts.length === 0) &&
      doesUserHaveOpenInvitations
    ) {
      window.location.href = '/#/login/manageAccess'
    } else if (queryParams && queryParams.redirect_url) {
      window.location.assign(decodeURIComponent(queryParams.redirect_url))
    } else if (doesUserHaveOpenInvitations) {
      window.location.href = '/#/login/manageAccess'
    } else {
      history.push('/retailer/accountSelector')
    }
  }

  const addJwtToState = (jwt) => {
    dispatch({
      type: ADD_JWT_TO_STATE,
      payload: {
        jwt,
      },
    })
  }

  const onSubmit = ({ email, password }, formikActions) => {
    Analytics.track('Login', { type: 'Email/Password' })

    //Traditional email/password login...
    signIn({
      variables: {
        email,
        password,
      },
    })
      .then((response) => {
        //Check if we have a pending connection to make
        if (pendingConnection) {
          //We need a JWT to proceed, get it with the response headers from login
          getJwt({
            variables: {
              ...response.data.signIn.headers,
            },
          })
            .then((getJwtResponse) => {
              //This ultimately updates the Apollo client with the JWT change
              addJwtToState(getJwtResponse.data.getJwt.jwt)
              //If we have a Facebook signed request, connect via Facebook...
              if (facebookSignedRequest) {
                connectUser({
                  variables: {
                    uuid: response.data.signIn.user.uuid,
                    signedRequest: facebookSignedRequest,
                  },
                })
                  .then(() => {
                    //User is connected, proceed to inform app we are logged in.
                    updateUserState(response.data.signIn)
                  })
                  .catch(() => {
                    enqueueSnackbar(I18n.t('auth.socialConnectionError'), {
                      variant: 'error',
                    })
                    formikActions.setSubmitting(false)
                  })
                setPendingLogin(false)
              } else {
                //Eventually we'd support other auth methods..
                //Pending connection, but no valid signed_request? Fallback to logging in
                updateUserState(response.data.signIn)
              }
            })
            .catch(() => {
              formikActions.setSubmitting(false)
              enqueueSnackbar(I18n.t('auth.socialConnectionError'), {
                variant: 'error',
              })
              setPendingLogin(false)
            })
        } else {
          //Normal login attempt, no social login attempt....
          updateUserState(response.data.signIn)
        }
      })
      .catch(() => {
        formikActions.setSubmitting(false)
        enqueueSnackbar(I18n.t('auth.loginError'), { variant: 'error' })
      })
  }

  if (pendingLogin) {
    return (
      <Box alignItems={'center'} display={'flex'} flexDirection={'column'}>
        <Loading />
        <Box mt={4}>
          <Typography color={'secondary'} variant={'h6'}>
            {pendingConnection
              ? I18n.t('auth.loggingInAndConnecting')
              : I18n.t('auth.loggingIn')}
          </Typography>
        </Box>
      </Box>
    )
  } else {
    return (
      <>
        <AuthCardHeader header={I18n.t('auth.promoboxxLogin')} />

        <Grid className={classes.wrapper} container={true} spacing={4}>
          {socialLoginEnabled && !pendingConnection && (
            <Grid item={true} sm={6} xs={12}>
              <Box className={classes.group}>
                <Typography color={'secondary'} variant={'subtitle2'}>
                  {I18n.t('auth.loginWithSocialAccount')}
                </Typography>
                {getFacebookAuthResults.loading ===
                  true /* Future social auth checks as well... */ && (
                  <Box className={classes.loading}>
                    <CircularProgress color={'primary'} />
                  </Box>
                )}
                <Box
                  className={
                    getFacebookAuthResults.loading === true
                      ? classes.hidden
                      : null
                  }
                  mt={4}
                  ref={facebookRef}
                >
                  <div
                    className={'fb-login-button'}
                    data-auto-logout-link={false}
                    data-button-type={'login_with'}
                    data-layout={'default'}
                    data-scope={permissions.join(',')}
                    data-size={'large'}
                    data-use-continue-as={false}
                  />
                </Box>
              </Box>
            </Grid>
          )}
          {pendingConnection && (
            <Grid item={true} sm={6} xs={12}>
              <Box className={classes.group}>
                <Typography color={'secondary'} variant={'subtitle2'}>
                  {I18n.t('auth.loginWithSocialAccount')}
                </Typography>
                <Box mr={8} mt={4}>
                  <Typography variant={'h6'}>
                    {I18n.t('auth.pendingConnectionTitle')}
                  </Typography>
                  <Box mb={4} mt={2}>
                    <Typography variant={'body2'}>
                      {I18n.t('auth.pendingConnectionBody')}
                    </Typography>
                  </Box>
                  <Typography variant={'caption'}>
                    {I18n.t('auth.pendingConnectionCaption')}
                    <Button
                      buttonStyle={'danger'}
                      onClick={onCancel}
                      size="small"
                      style={{ marginLeft: '10px' }}
                    >
                      {I18n.t('shared.cancel')}
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          <Grid item={true} sm={socialLoginEnabled ? 6 : 12} xs={12}>
            <Box className={classes.group}>
              <Typography color={'secondary'} variant={'subtitle2'}>
                {I18n.t('auth.loginWithEmailPassword')}
              </Typography>
              <Box mt={4}>
                <Formik
                  enableReinitialize
                  initialValues={{
                    email: '',
                    password: '',
                  }}
                  onSubmit={onSubmit}
                  validationSchema={schema}
                >
                  {({ isSubmitting }) => {
                    return (
                      <Form>
                        <Field
                          className={classes.input}
                          component={TextField}
                          inputProps={{
                            'data-testid': 'emailInput',
                          }}
                          label={I18n.t('shared.emailAddress')}
                          name={'email'}
                          placeholder={I18n.t('shared.emailAddress')}
                          type={'email'}
                        />

                        <Field
                          className={classes.input}
                          component={TextField}
                          inputProps={{
                            'data-testid': 'passwordInput',
                          }}
                          label={I18n.t('shared.password')}
                          name={'password'}
                          placeholder={I18n.t('shared.password')}
                          type={showPassword ? 'text' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                onClick={() => setShowPassword(!showPassword)}
                                position="end"
                              >
                                <IconButton size="large">
                                  {showPassword ? (
                                    <VisibilityOutlined />
                                  ) : (
                                    <VisibilityOffOutlined />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Box className={classes.footer}>
                          <LinkChild to="/forgot-password">
                            <Link>{I18n.t('auth.forgotPassword')}</Link>
                          </LinkChild>
                          <Button
                            data-testid={'loginButton'}
                            disabled={isSubmitting}
                            id={'loginButton'}
                            loading={isSubmitting}
                            name={'loginButton'}
                            type={'submit'}
                          >
                            {pendingConnection
                              ? I18n.t('auth.loginAndConnect')
                              : I18n.t('auth.login')}
                          </Button>
                        </Box>
                      </Form>
                    )
                  }}
                </Formik>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </>
    )
  }
}

export default LoginForm
